import { Box, Button, Stack } from '@mui/material';
import { useState } from 'react';
import OrdersTableIdCol from '../../../components/orders/OrdersTableIdCol';
import OrdersTableTypeCol from '../../../components/orders/OrdersTableTypeCol';
import FullScreenModal from '../../../components/ui/modal/FullScreenModal';
import useModal from '../../../components/ui/modal/useModal';
import PaginatedTable from '../../../components/ui/tables/paginated-table/PaginatedTable';
import { ViewOrderDto } from '../../../lib/api/orders/dto/view-order.dto';
import { OrderStatus } from '../../../lib/api/orders/enums/order-status.enum';
import { getOrderById } from '../../../lib/api/orders/orders.service';
import {
  createInvoiceAndLoadingListPdf,
  createInvoicePdf,
  createLoadingListPdf,
} from '../../../lib/common/pdf.service';
import { TableCol } from '../../../lib/util/table-util';
import OrderConfirmationPage from '../../orders/[id]/OrderConfirmationPage';
import {
  OrderActionsProps,
  OrderFullfillmentTableProps,
} from './shared.interfaces';

export default function OrdersBacklogTable({
  apiRes,
  isFetching,
  setTableState,
  tableState,
  onRowClick,
  onChangeStatus,
}: OrderFullfillmentTableProps) {
  const { handleCloseModal, handleLaunchModal, modalOpen } = useModal();
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const columns: TableCol<ViewOrderDto>[] = [
    {
      id: 'id',
      label: 'ID',
      filterType: 'contains',
      render: (order: ViewOrderDto) => (
        <OrdersTableIdCol order={order} onClick={handleOrderIdClicked} />
      ),
      widthPct: 10,
      sortable: true,
      sortKey: 'createdAt',
    },
    {
      id: 'orderWeek',
      label: 'Order Week',
      filterType: 'contains',
      widthPct: 15,
      headerCellSx: { minWidth: '115px' },
    },
    {
      id: 'deliveryRouteName',
      label: 'Route',
      filterType: 'contains',
      widthPct: 15,
      headerCellSx: { minWidth: '115px' },
    },
    {
      id: 'billingCompanyName',
      label: 'Bill to',
      filterType: 'contains',
      widthPct: 15,
      headerCellSx: { minWidth: '115px' },
    },
    {
      id: 'shippingCompanyName',
      label: 'Ship to',
      filterType: 'contains',
      widthPct: 15,
      headerCellSx: { minWidth: '115px' },
    },
    {
      id: 'type',
      label: 'Type',
      filterType: 'contains',
      render: (order: ViewOrderDto) => <OrdersTableTypeCol order={order} />,
      widthPct: 5,
      align: 'center',
    },
    {
      id: 'racks',
      label: 'Racks',
      render: (order: ViewOrderDto) => <>{order.racks.toFixed(2)}</>,
      widthPct: 10,
    },
    {
      // @ts-ignore
      id: 'actions',
      label: 'Action',
      render: (o: ViewOrderDto) => (
        <BacklogOrderActions order={o} onChangeStatus={onChangeStatus} />
      ),
      widthPct: 15,
    },
  ];

  const handleOrderIdClicked = (e: any, order: ViewOrderDto) => {
    e.stopPropagation();
    setSelectedOrderId(order.id);
    handleLaunchModal();
  };

  return (
    <>
      <PaginatedTable<ViewOrderDto>
        columns={columns}
        apiRes={apiRes}
        isFetching={isFetching}
        setTableState={setTableState}
        tableState={tableState}
        onRowClick={onRowClick}
        paperElevation={0}
        hoverDisabled
        fullWidth
      />
      <FullScreenModal
        title="Order"
        open={modalOpen}
        onClose={handleCloseModal}
      >
        <Box sx={{ p: 3 }}>
          {selectedOrderId && (
            <OrderConfirmationPage orderId={selectedOrderId} />
          )}
        </Box>
      </FullScreenModal>
    </>
  );
}

function BacklogOrderActions({ order, onChangeStatus }: OrderActionsProps) {
  const handlePrintInvoice = async (orderId: string) => {
    const orderExtended = await getOrderById(orderId);
    createInvoicePdf(orderExtended);
  };

  const handlePrintLoadingList = async (orderId: string) => {
    const orderExtended = await getOrderById(orderId);
    createLoadingListPdf(orderExtended);
  };

  const handlePrintLoadingListAndInvoice = async (orderId: string) => {
    const orderExtended = await getOrderById(orderId);
    createInvoiceAndLoadingListPdf(orderExtended);
  };

  return (
    <Stack flexDirection="column" gap={1}>
      <Button
        variant="contained"
        size="small"
        sx={{ minWidth: '150px' }}
        onClick={(e) => onChangeStatus(e, order.id, OrderStatus.staging)}
      >
        Move to Staging
      </Button>
      <Button
        variant="outlined"
        size="small"
        sx={{ minWidth: '150px' }}
        onClick={() => handlePrintInvoice(order.id)}
      >
        Print Invoice
      </Button>
      <Button
        variant="outlined"
        size="small"
        sx={{ minWidth: '150px' }}
        onClick={() => handlePrintLoadingList(order.id)}
      >
        Print Loading List
      </Button>
      <Button
        variant="outlined"
        size="small"
        sx={{ minWidth: '150px' }}
        onClick={() => handlePrintLoadingListAndInvoice(order.id)}
      >
        Print Both
      </Button>
    </Stack>
  );
}
